import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MailOutlined } from "@ant-design/icons";
import { Button, Form, Input, Spin, theme } from "antd";
import { observer } from "mobx-react";
import { useEffect } from "react";
import { MagicLinkConfirmation } from "./magicLinkConfirmation.js";
import { IMPREZA_BUTTON_PROPS } from "../../../../components/provider.js";
import { RealCommerceError } from "../../../../exceptions/realCommerceError.js";
import { NotificationChannel } from "../../../../store/notification.js";
import { useStores } from "../../../../store/stores.js";
import { __ } from "../../../../utils/i18n.js";
const INITIAL_VALUES = {
    email: ""
};
// Magic-link sign-in form. On success the store's `submitted` observable
// flips and the confirmation card renders in place of the form.
//
// JWT bootstrap (URL `?magic-link-token=…` + stored-JWT restore + DOI code)
// is owned by `app.tsx` — it runs above this component so the hydration
// skeleton can render before any unauth chrome is shown for returning users.
const MagicLinkSignInForm = observer(()=>{
    const { accountV2Store, notificationStore } = useStores();
    const [form] = Form.useForm();
    const { token } = theme.useToken();
    const onFinish = async ({ email })=>{
        notificationStore.clearChannel(NotificationChannel.accountV2Unauth);
        try {
            await accountV2Store.sendMagicLink(email);
        } catch (e) {
            if (e instanceof RealCommerceError) {
                Array.from(e.errors).forEach(({ message: msg })=>{
                    notificationStore.add({
                        type: "error",
                        message: msg,
                        channel: NotificationChannel.accountV2Unauth,
                        closable: true
                    });
                });
            }
        }
    };
    // Empty the email field whenever the confirmation card is dismissed back
    // to the form, so a fresh attempt starts with a blank input instead of
    // the previously-submitted address.
    useEffect(()=>{
        if (!accountV2Store.submitted) {
            form.resetFields();
        }
    }, [
        accountV2Store.submitted,
        form
    ]);
    const isBusy = !!accountV2Store.session?.isBusy;
    if (accountV2Store.submitted) {
        return /*#__PURE__*/ _jsx(MagicLinkConfirmation, {
            email: accountV2Store.submitted.email
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "rc-account-v2-auth-card-wrap",
        children: /*#__PURE__*/ _jsx("div", {
            className: "rc-account-v2-auth-card",
            style: {
                background: token.colorBgContainer,
                borderColor: token.colorBorder
            },
            children: /*#__PURE__*/ _jsxs(Spin, {
                spinning: isBusy,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "rc-account-v2-auth-card__icon",
                        style: {
                            color: token.colorPrimary
                        },
                        children: /*#__PURE__*/ _jsx(MailOutlined, {})
                    }),
                    /*#__PURE__*/ _jsx("h1", {
                        className: "rc-account-v2-auth-card__title",
                        style: {
                            color: token.colorTextHeading
                        },
                        children: __("Sign in to your account")
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "rc-account-v2-auth-card__subtitle",
                        style: {
                            color: token.colorTextSecondary
                        },
                        children: __("Request a magic link to access your subscriptions, licenses, and invoices.")
                    }),
                    /*#__PURE__*/ _jsxs(Form, {
                        form: form,
                        onFinish: onFinish,
                        layout: "vertical",
                        initialValues: INITIAL_VALUES,
                        requiredMark: false,
                        children: [
                            /*#__PURE__*/ _jsx(Form.Item, {
                                name: "email",
                                rules: [
                                    {
                                        type: "email",
                                        required: true,
                                        message: __("Please enter a valid email address.")
                                    }
                                ],
                                children: /*#__PURE__*/ _jsx(Input, {
                                    size: "large",
                                    prefix: /*#__PURE__*/ _jsx(MailOutlined, {
                                        className: "rc-account-v2-auth-card__input-icon",
                                        style: {
                                            color: token.colorTextSecondary
                                        }
                                    }),
                                    placeholder: __("Your email address")
                                })
                            }),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                className: "rc-account-v2-auth-card__cta",
                                children: /*#__PURE__*/ _jsx(Button, {
                                    type: "primary",
                                    size: "large",
                                    htmlType: "submit",
                                    block: true,
                                    ...IMPREZA_BUTTON_PROPS,
                                    children: __("Send magic link")
                                })
                            })
                        ]
                    })
                ]
            })
        })
    });
});
export { MagicLinkSignInForm };
